<template>
  <div class="hcl-tiles-editor-chooser">
    <div class="tiles-chooser-wrapper">
      <template v-for="(tile, idx) in tiles.tiles">
        <div class="tile-outer" v-if="tile.chooser" :key="tile.id">
          <div class="tile-inner" :class="{ dragging: dragging.active && idx === dragging.index }"
            @mousedown.left.prevent.stop="mousedown(idx, $event)">
            <component :is="componentName(tile)" :tile="tile" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

import HclTilesEditorChooserContentFormElement from '@/components/TilesEditorChooserContentFormElement/TilesEditorChooserContentFormElement.vue'
import HclTilesEditorChooserContentEmpty from '@/components/TilesEditorChooserContentEmpty/TilesEditorChooserContentEmpty.vue'

import { DefinitionTile } from '@/components/types'

interface Props {
  /**
   * Definition as an JS object.
   */
  tiles: any
  dimensions: any
  dragging: any
}

const props = withDefaults(defineProps<Props>(), {
  tiles: undefined,
  dimensions: undefined,
  dragging: undefined
})

const dragElement = ref()
const dX = ref(0)
const dY = ref(0)

const units = computed(() => {
  return {
    height: props.dimensions.height / props.tiles.dimensions.rows.current,
    width: props.dimensions.width / props.tiles.dimensions.cols.current
  }
})

const dragMove = (e) => {
  dragElement.value.style.left = e.clientX + dX.value + 'px'
  dragElement.value.style.top = e.clientY + dY.value + 'px'
}

const emit = defineEmits(['dragging-change-active', 'dragging-change-cancelled', 'dragging-change-all'])

const dragStop = () => {
  document.documentElement.removeEventListener('mousemove', dragMove, false)
  document.documentElement.removeEventListener('mouseup', dragStop, false)
  document.documentElement.removeEventListener('keydown', keyDown, false)

  dragElement.value.parentElement.removeAttribute('style')
  dragElement.value.removeAttribute('style')
  dragElement.value = null

  emit('dragging-change-active', false)
}

const keyDown = (e: any) => {
  if (e.keyCode === 27) {
    emit('dragging-change-cancelled', true)
    dragStop()
  }
}

const mousedown = (idx: any, e: any) => {
  if (!document.defaultView) {
    return
  }

  const cs = document.defaultView.getComputedStyle(e.target)
  const bounds = e.target.getBoundingClientRect()

  dX.value = bounds.left - e.clientX
  dY.value = bounds.top - e.clientY

  dragElement.value = e.target

  dragElement.value.style.width = props.tiles.tiles[idx].w * units.value.width + 'px'
  dragElement.value.style.height = dragElement.value.parentElement.style.height =
    parseFloat(cs.height) + 'px'

  dragMove(e)

  document.documentElement.addEventListener('mousemove', dragMove, false)
  document.documentElement.addEventListener('mouseup', dragStop, false)
  document.documentElement.addEventListener('keydown', keyDown, false)

  emit('dragging-change-all', {
    active: true,
    cancelled: false,
    index: idx
  })
}

const componentName = (tile: DefinitionTile) => {
  if (tile.entity.type === 'FormElement') {
    return HclTilesEditorChooserContentFormElement
  }

  return HclTilesEditorChooserContentEmpty
}
</script>

<style lang="scss">
.hcl-tiles-editor-chooser {
  padding: 5px;
  border: 1px solid #ccc;
  min-height: 55px;

  .tiles-chooser-wrapper {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
    overflow: hidden;

    .tile-outer {
      margin: 0 3px 3px 0;

      .tile-inner {
        padding: 10px 5px;
        border: 1px solid #ccc;
        background: #f4f4f4;
        text-align: center;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        width: 155px;

        &.dragging {
          pointer-events: none;
          position: fixed;
          z-index: 1000;
          opacity: 0.8;
        }

        .tile-text {
          font-weight: 500;
        }
      }
    }
  }
}
</style>
